/* ================================================================
 * Tour stop · The per-tick state hash
 * ----------------------------------------------------------------
 * Encodes every gameplay-affecting field of `MatchState` into one
 * 32-bit hash per tick. Replays and the golden tests compare these
 * hashes to pin deterministic behavior — if two runs diverge, the
 * first differing tick localizes the bug.
 *
 * The rule (AGENTS.md): any new state that affects gameplay MUST be
 * mixed in here, and enum-ish string unions are encoded through a
 * `*ToInt` mapper so the hash never depends on string contents
 * shifting. Field order is part of the hash — append, don't reorder.
 * ================================================================ */

import { createHasher, finalize, mixFloat, mixInt, mixString } from "../math/Hash.js";
import { faceoffTargetToInt } from "../rules/faceoff.js";
import { faceoffSpotToInt } from "../state/faceoff-spots.js";
import type {
  FaceoffIntentTracker,
  GoalieState,
  MatchClock,
  MatchState,
  PlayerState,
  PuckState,
  RulesState,
  TeamControlState,
} from "../state/types.js";

export function computeHash(state: MatchState): number {
  const h = createHasher();
  mixInt(h, state.tick);
  mixInt(h, state.clock.periodIndex);
  mixInt(h, state.clock.remainingTicks);
  mixInt(h, phaseToInt(state.clock.phase));
  // `nextPlayPhase` is genuinely optional on the type — only set
  // during the OT-faceoff window. 0 = undefined, 1 = "Play", 2 = "Overtime".
  mixInt(
    h,
    state.clock.nextPlayPhase === undefined ? 0 : state.clock.nextPlayPhase === "Overtime" ? 2 : 1,
  );
  mixInt(h, state.score.home);
  mixInt(h, state.score.away);
  mixFloat(h, state.puck.pos.x);
  mixFloat(h, state.puck.pos.y);
  mixFloat(h, state.puck.vel.x);
  mixFloat(h, state.puck.vel.y);
  mixInt(h, puckModeToInt(state.puck.mode));
  mixInt(h, state.puck.noRecaptureUntilTick);
  mixInt(h, state.puck.opponentLockoutUntilTick);
  mixString(h, state.puck.opponentLockoutPlayerId ?? "");
  mixInt(h, state.puck.faceoffCenterLockoutUntilTick);

  mixInt(h, state.players.length);
  for (const p of state.players) {
    mixString(h, p.id);
    mixInt(h, roleToInt(p.position));
    mixFloat(h, p.pos.x);
    mixFloat(h, p.pos.y);
    mixFloat(h, p.vel.x);
    mixFloat(h, p.vel.y);
    mixFloat(h, p.faceDir.x);
    mixFloat(h, p.faceDir.y);
    mixFloat(h, p.stickDir.x);
    mixFloat(h, p.stickDir.y);
    mixInt(h, actionToInt(p.action));
    mixInt(h, p.actionTimer);
    mixInt(h, p.stunTicks);
    mixInt(h, p.recoveryTicks);
    mixFloat(h, p.stamina);
    mixInt(h, p.hasPuck ? 1 : 0);
    mixFloat(h, p.possessionQuality);
    mixInt(h, p.shootHoldTicks);
    mixInt(h, p.passHoldTicks);
    mixInt(h, p.receivedPassFromTick);
    mixInt(h, p.isProtecting ? 1 : 0);
    mixInt(h, p.benched ? 1 : 0);
  }

  mixInt(h, state.goalies.length);
  for (const goalie of state.goalies) {
    mixString(h, goalie.id);
    mixFloat(h, goalie.pos.x);
    mixFloat(h, goalie.pos.y);
    mixFloat(h, goalie.vel.x);
    mixFloat(h, goalie.vel.y);
    mixFloat(h, goalie.facing.x);
    mixFloat(h, goalie.facing.y);
    mixInt(h, goalieModeToInt(goalie.mode));
    mixInt(h, goalie.modeTimer);
    mixInt(h, goalie.pulled ? 1 : 0);
  }

  mixInt(h, state.control.length);
  for (const team of state.control) {
    mixString(h, team.teamId);
    mixString(h, team.manualGoalieSeat ?? "");
    mixInt(h, team.goaliePullToggleHeldLastTick ? 1 : 0);
    mixInt(h, tacticToInt(team.tactic));
  }

  hashRulesInto(state.rules, h);

  mixInt(h, state.rng.state);
  return finalize(h);
}

function hashRulesInto(rules: RulesState, h: ReturnType<typeof createHasher>): void {
  // Per-rule hash contribution. Order matters for determinism — keep
  // fields in alphabetical order so two slices touching the hash produce
  // stable diffs.
  //
  // Each field is genuinely optional on `RulesState` — a rule's scratch
  // record only exists once that rule has fired. The presence check
  // here lets the hash distinguish a never-fired rule (absent) from a
  // fired-with-zero-values rule (present), since the absent branch is
  // a no-mix and the present branch emits a structural mix.
  if (rules.faceoffIntents !== undefined) {
    hashTrackerInto(rules.faceoffIntents.home, h);
    hashTrackerInto(rules.faceoffIntents.away, h);
  }
  if (rules.nextFaceoffSpot !== undefined) {
    mixInt(h, faceoffSpotToInt(rules.nextFaceoffSpot));
  }
  if (rules.icing !== undefined) {
    mixString(h, rules.icing.team);
    mixString(h, rules.icing.shooterId);
    mixFloat(h, rules.icing.shotFromY);
  }
  if (rules.offside !== undefined) {
    mixString(h, rules.offside.team);
    mixInt(h, rules.offside.potentialIds.length);
    for (const pid of rules.offside.potentialIds) mixString(h, pid);
    mixFloat(h, rules.offside.puckEnterX);
    mixFloat(h, rules.offside.puckEnterY);
  }
  if (rules.puckOutOfPlay !== undefined) {
    mixInt(h, rules.puckOutOfPlay.ticks);
  }
  if (rules.shootout !== undefined) {
    const so = rules.shootout;
    mixInt(h, so.round);
    mixString(h, so.shooterTeam);
    mixString(h, so.shooterId);
    mixInt(h, shootoutSubPhaseToInt(so.subPhase));
    mixInt(h, so.subPhaseEndTick);
    mixInt(h, shootoutResultToInt(so.lastResult));
    mixInt(h, so.score.home);
    mixInt(h, so.score.away);
    mixInt(h, so.attempts.home);
    mixInt(h, so.attempts.away);
    mixInt(h, so.shotTaken ? 1 : 0);
    mixInt(h, so.shotTakenAtTick);
    // maxPuckForward can be -Infinity at attempt start. Mix a
    // sentinel for that to keep the hash deterministic.
    mixFloat(h, Number.isFinite(so.maxPuckForward) ? so.maxPuckForward : -9999);
  }
}

function hashTrackerInto(
  t: FaceoffIntentTracker | undefined,
  h: ReturnType<typeof createHasher>,
): void {
  if (t === undefined) {
    mixInt(h, 0); // absent sentinel
    return;
  }
  mixInt(h, 1); // present sentinel
  mixInt(h, faceoffTargetToInt(t.currentTarget));
  mixInt(h, t.targetSetAtTick);
  mixInt(h, t.passHeldLastTick ? 1 : 0);
  if (t.commit === null) {
    mixInt(h, 0);
  } else {
    mixInt(h, 1);
    mixInt(h, faceoffTargetToInt(t.commit.target));
    mixInt(h, t.commit.commitTick);
    mixInt(h, t.commit.stabilityTicks);
  }
}

function shootoutSubPhaseToInt(p: "Setup" | "Shoot" | "Result"): number {
  switch (p) {
    case "Setup":
      return 0;
    case "Shoot":
      return 1;
    case "Result":
      return 2;
  }
}

function shootoutResultToInt(r: "Goal" | "Miss" | "None"): number {
  switch (r) {
    case "None":
      return 0;
    case "Goal":
      return 1;
    case "Miss":
      return 2;
  }
}

function tacticToInt(tactic: TeamControlState["tactic"]): number {
  switch (tactic) {
    case "balanced":
      return 0;
    case "trap":
      return 1;
    case "aggressive":
      return 2;
  }
}

function roleToInt(role: PlayerState["position"]): number {
  switch (role) {
    case "LW":
      return 0;
    case "C":
      return 1;
    case "RW":
      return 2;
    case "LD":
      return 3;
    case "RD":
      return 4;
  }
}

function actionToInt(action: PlayerState["action"]): number {
  switch (action) {
    case "Idle":
      return 0;
    case "WindupPass":
      return 1;
    case "WindupShoot":
      return 2;
    case "WindupCheck":
      return 3;
    case "RecoverCheck":
      return 4;
    case "Stunned":
      return 5;
  }
}

function goalieModeToInt(mode: GoalieState["mode"]): number {
  switch (mode) {
    case "Ready":
      return 0;
    case "Tracking":
      return 1;
    case "Saving":
      return 2;
    case "Recovering":
      return 3;
  }
}

function phaseToInt(phase: MatchClock["phase"]): number {
  switch (phase) {
    case "Faceoff":
      return 0;
    case "Play":
      return 1;
    case "GoalCelebration":
      return 2;
    case "PeriodBreak":
      return 3;
    case "GameOver":
      return 4;
    case "Stoppage":
      return 5;
    case "Overtime":
      return 6;
    case "Shootout":
      return 7;
  }
}

function puckModeToInt(mode: PuckState["mode"]): number {
  switch (mode) {
    case "Loose":
      return 0;
    case "Controlled":
      return 1;
    case "Pass":
      return 2;
    case "Shot":
      return 3;
  }
}
